import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Card, CardHeader, ContentLoader, Input } from '@/components/common';
import { partnerLoginAdminApi } from '@/services/partnerPortal';

/**
 * Broker admin: give a partner channel a real login, re-send the set-password link, or revoke it.
 * Shown on a PARTNER-type channel only (the server refuses any other kind).
 */
export function PartnerLoginPanel({ channelId }: { channelId: string }) {
  const qc = useQueryClient();
  const key = ['partner-login', channelId];
  const status = useQuery({ queryKey: key, queryFn: () => partnerLoginAdminApi.status(channelId), retry: false });
  const [email, setEmail] = useState('');
  const [link, setLink] = useState<string | null>(null);
  const done = (r?: { invite_url: string }) => {
    setLink(r?.invite_url ?? null);
    qc.invalidateQueries({ queryKey: key });
  };
  const create = useMutation({ mutationFn: () => partnerLoginAdminApi.create(channelId, { email }), onSuccess: done });
  const resend = useMutation({ mutationFn: () => partnerLoginAdminApi.resend(channelId), onSuccess: done });
  const revoke = useMutation({ mutationFn: () => partnerLoginAdminApi.revoke(channelId), onSuccess: () => done() });

  if (status.isLoading) return <ContentLoader />;
  if (status.isError || !status.data || (!status.data.eligible && !status.data.has_login)) return null;
  const s = status.data;

  return (
    <Card>
      <CardHeader title="Partner login" subtitle="Lets this partner sign in and see only the agents they invited." />
      {s.has_login ? (
        <div className="space-y-3 text-sm text-gray-900 dark:text-gray-100">
          <p>
            {s.email} · {s.status === 'active' ? 'Active' : 'Revoked'}
          </p>
          {s.status === 'active' && (
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => resend.mutate()} isLoading={resend.isPending}>Re-send set-password link</Button>
              <Button variant="danger" onClick={() => revoke.mutate()} isLoading={revoke.isPending}>Revoke login</Button>
            </div>
          )}
        </div>
      ) : (
        <form
          className="space-y-3"
          onSubmit={(e) => {
            e.preventDefault();
            create.mutate();
          }}
        >
          <Input label="Partner's email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
          {create.isError && <p role="alert" className="text-sm text-red-600 dark:text-red-400">Could not create the login. The email may already be in use.</p>}
          <Button type="submit" isLoading={create.isPending}>Create login and send invite</Button>
        </form>
      )}
      {link && <p role="status" className="mt-3 break-all text-sm text-gray-700 dark:text-gray-300">Invite link (shown once): <span className="font-mono">{link}</span></p>}
    </Card>
  );
}
